import { useEffect, useState, useSyncExternalStore } from 'react';
import { useChatStore } from '../../../store/chatStore';
import { getRoom } from '../../../api/chat';
import type { ChatRoomDto, RoomMemberDto } from '../../../types/chat';
import type { SseStatus } from '../../../hooks/useSSE';
import { MessageList } from './MessageList';
import { MessageInput } from './MessageInput';
import { ChatEmptyHero } from './ChatEmptyHero';
import { ChatTypeWatermark, type WatermarkKind } from './ChatTypeWatermark';
import { ChatDetailPanel } from './ChatDetailPanel';
import { ChatNoticeBanner } from './ChatNoticeBanner';
// chat-room-name / chat-display-names — 방 이름은 한 곳에서 정하고 아래로 내려보낸다.
import { resolveRoomName } from '../../../lib/chat-room-name';
import { useChatDisplayNameStore, myNameFor } from '../../../store/chatDisplayNameStore';
import { useAuthStore } from '../../../store/authStore';
// chat-room-styles — 방마다 내가 고른 배경·말풍선 색. 앱과 같은 카탈로그를 쓴다.
import { useChatRoomStyleStore, styleOf } from '../../../store/chatRoomStyleStore';
// chat-polls — 방의 투표를 받아 두면 MessageItem 이 messageId 로 카드 자리를 찾는다.
import { useChatPollStore } from '../../../store/chatPollStore';
import { backgroundCss, backgroundPreset, accentHex } from '../../../lib/chat-room-style';
import { ChatDocPreviewPanel } from './ChatDocPreviewPanel';
import { useChatPreviewStore } from '../../../store/chatPreviewStore';
import { tChat, getChatLang, subscribeChatLang } from '../../../lib/chat-i18n';

interface ChatAreaProps {
  room: ChatRoomDto | null;
  sseStatus: SseStatus;
  /** 상단 '이전 대화 불러오기' 액션 — MessageList 로 전달. */
  onLoadOlder?: (roomId: string) => void;
}

/**
 * ChatArea (P2-16) — Header + StatusBanner + MessageList + MessageInput 컴포지션.
 *
 * room === null 이면 EmptyState (시안에 명시적 비활성 상태 없음 — UX 보강).
 * sseStatus 가 reconnecting/exhausted 면 status banner 표시.
 */
export function ChatArea({ room, sseStatus, onLoadOlder }: ChatAreaProps) {
  useSyncExternalStore(subscribeChatLang, getChatLang, getChatLang);
  const activeChatId = useChatStore((s) => s.activeChatId);
  const setRoomMemberCount = useChatStore((s) => s.setRoomMemberCount);
  const [members, setMembers] = useState<RoomMemberDto[]>([]);
  const [memberError, setMemberError] = useState<string | null>(null);
  // chat-detail-panel — 상세보기 패널에서 방 이름·설명을 고치면 여기에 덮어써서 즉시 반영한다.
  //   room 은 상위(ChatPage)가 쥔 prop 이라 위로 올리지 않고 화면 표시만 앞당긴다.
  //   좌측 대화 목록은 다음 조회에서 따라온다.
  const [profileEdit, setProfileEdit] = useState<{ name?: string; description?: string | null } | null>(null);
  // chat-display-names — 내가 부르는 이름. 처음 한 번만 받아 두고 화면 전체가 같은 값을 쓴다.
  const myId = useAuthStore((s) => s.user?.id?.toString() ?? '');
  const displayNames = useChatDisplayNameStore((s) => s.names);
  const loadDisplayNames = useChatDisplayNameStore((s) => s.load);
  useEffect(() => { void loadDisplayNames(); }, [loadDisplayNames]);
  // chat-room-styles — 꾸밈도 한 번만 받아 둔다.
  const roomStyles = useChatRoomStyleStore((s) => s.styles);
  const loadRoomStyles = useChatRoomStyleStore((s) => s.load);
  useEffect(() => { void loadRoomStyles(); }, [loadRoomStyles]);

  // dependency 를 room?.id 로 좁힘 — rooms 배열 변경 (SSE 도착으로 인한 lastMessageAt 업데이트
  // 가 ChatPage 의 setRooms 로 전파될 미래 시점에) 마다 getRoom 재호출 회피. (review HIGH-3 봉합)
  // 방 전환 시 다른 방의 문서 미리보기는 닫는다 (chat-doc-preview, additive).
  const closeIfNotRoom = useChatPreviewStore((s) => s.closeIfNotRoom);
  const roomId = room?.id;

  // chat-polls — 방을 열면 그 방의 투표를 받는다.
  const loadPolls = useChatPollStore((s) => s.load);
  useEffect(() => {
    if (roomId) void loadPolls(roomId);
  }, [roomId, loadPolls]);

  // 남이 만든 투표가 대화에 도착하면(SSE) 카드가 뜨도록 목록을 다시 받는다.
  // 메시지 형식에 투표 표식을 넣지 않았으므로, 서버가 남긴 사람이 읽는 문장으로 알아본다.
  const roomMessages = useChatStore((s) => (roomId ? s.messagesByRoom[roomId] : undefined));
  const knownPolls = useChatPollStore((s) => (roomId ? s.pollsByRoom[roomId] : undefined));
  const lastMessage = roomMessages?.[roomMessages.length - 1];
  useEffect(() => {
    if (!roomId || !lastMessage) return;
    if (!lastMessage.content?.startsWith('[투표] ')) return;
    // 이미 아는 투표면 다시 받을 이유가 없다.
    if (knownPolls?.some((p) => p.messageId === lastMessage.id)) return;
    void loadPolls(roomId, { force: true });
  }, [roomId, lastMessage, knownPolls, loadPolls]);
  useEffect(() => {
    closeIfNotRoom(roomId ?? null);
  }, [roomId, closeIfNotRoom]);

  useEffect(() => {
    if (!roomId) {
      setMembers([]);
      return;
    }
    let cancelled = false;
    setMemberError(null);
    getRoom(roomId)
      .then((res) => {
        if (cancelled) return;
        setMembers(res.members);
        // ChatList active 카드가 멤버 수 표시 — chatStore SSOT 로 공유.
        setRoomMemberCount(roomId, res.members.length);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setMemberError(err instanceof Error ? err.message : 'room_detail_failed');
      });
    return () => {
      cancelled = true;
    };
  }, [roomId, setRoomMemberCount]);

  if (!room || !activeChatId) {
    return (
      <section className="chat-area chat-area-empty" aria-label={tChat('chat.area.aria_empty')}>
        <ChatEmptyHero />
      </section>
    );
  }

  // v2.11 — 방 종류별 배경 워터마크. AI 방 = 본인만 멤버(ChatList isAiRoom 과 동일 신호).
  const wmKind: WatermarkKind = (room.memberCount ?? members.length) === 1
    ? 'ai'
    : room.type === 'direct' ? 'direct' : 'group';

  // 내가 부르는 이름 → 공용 이름 → 기본(1:1 은 상대 이름 · 그 밖은 단체대화방).
  // 패널에서 이름을 고치면 profileEdit 이 먼저 반영되고, 목록은 다음 조회에서 따라온다.
  const shownRoomName = resolveRoomName({
    type: room.type,
    name: profileEdit?.name ?? room.name,
    members,
    myId,
    myName: myNameFor(displayNames, 'room', room.id),
    fallback: tChat('chat.area.fallback_room_name'),
  });

  // 이 방의 꾸밈 — 배경은 CSS 변수로 흘려보내고, 말풍선 색도 같은 방식으로 준다.
  // 변수로 두는 이유: 말풍선·입력창은 저마다 다른 컴포넌트가 그리는데, 색 하나 때문에
  // 그 컴포넌트들의 시그니처를 바꾸고 싶지 않다(기존 렌더 경로 무수정).
  const style = styleOf(roomStyles, room.id);
  const bgCss = backgroundCss(style.backgroundId);
  const bgPreset = backgroundPreset(style.backgroundId);
  const accent = accentHex(style.accentId);
  const roomStyleVars: Record<string, string> = {};
  if (bgCss) roomStyleVars['--chat-room-bg'] = bgCss;
  if (accent) roomStyleVars['--chat-room-accent'] = accent;

  // chat-participants / chat-doc-preview (2026-06-30) — 대화 영역을 shell 로 감싸 우측에 참가자 바
  // (고정) + 문서 미리보기 패널(슬라이드인)을 additive 로 배치. 기존 .chat-area 본문은 무변경.
  return (
    <div className="chat-area-shell">
      <section
        className={`chat-area chat-area--wm${bgCss ? ' chat-area--styled' : ''}${bgPreset?.dark ? ' chat-area--styled-dark' : ''}`}
        style={roomStyleVars as React.CSSProperties}
        aria-label={`${shownRoomName} ${tChat('chat.area.aria_active_suffix')}`}
      >
        <ChatTypeWatermark key={room.id} kind={wmKind} />
        {(sseStatus === 'reconnecting' || sseStatus === 'exhausted' || sseStatus === 'unauthorized') && (
          <div
            className={`chat-area-status-banner${
              sseStatus === 'exhausted' || sseStatus === 'unauthorized' ? ' chat-area-status-banner--exhausted' : ''
            }`}
            role="status"
          >
            {sseStatus === 'reconnecting'
              ? tChat('chat.area.sse_reconnecting')
              : sseStatus === 'unauthorized'
                ? tChat('chat.area.sse_unauthorized')
                : tChat('chat.area.sse_exhausted')}
          </div>
        )}
        {memberError && (
          <div className="chat-area-status-banner" role="status">
            {tChat('chat.area.member_load_failed', { msg: memberError })}
          </div>
        )}
        {/* room-notice — 방 공지 띠. 공지가 없으면 아무것도 그리지 않아 기존 배치에 영향이 없다. */}
        <ChatNoticeBanner roomId={room.id} />
        {/* chat-read-receipts — roomType/memberCount 는 1:1 과 그룹의 읽음 표시 문구를 가른다. */}
        <MessageList
          roomId={room.id}
          members={members}
          onLoadOlder={onLoadOlder}
          roomType={room.type}
          memberCount={room.memberCount ?? members.length}
        />
        <MessageInput roomId={room.id} />
      </section>
      <ChatDocPreviewPanel roomId={room.id} />
      {/* chat-detail-panel(2026-08-17 확정 시안) — 종전 ChatParticipantPanel 을 대체한다.
          초대 모달·미디어 브라우저는 그대로 재사용하므로 그 두 기능은 무회귀. */}
      <ChatDetailPanel
        roomId={room.id}
        roomType={room.type}
        roomName={shownRoomName}
        roomDescription={profileEdit?.description ?? room.description ?? null}
        members={members}
        onMembersChanged={(next) => {
          setMembers(next);
          setRoomMemberCount(room.id, next.length);
        }}
        onRoomProfileChanged={(patch) => setProfileEdit((cur) => ({ ...cur, ...patch }))}
      />
    </div>
  );
}
